import * as THREE from 'three';
import { SHARED_VERTEX_SHADER, SHARED_FRAGMENT_SHADER } from '../core/ParticleSystem.js';

/**
 * Landscape Scene - Image-based particle visualization
 * Particles form a detailed landscape image
 */
export class LandscapeScene {
  constructor(count = 5000) {
    this.count = count;
    this.geometry = null;
    this.material = null;
    this.points = null;
    this.width = 0;
    this.height = 0;
    this.originalColors = null;

    this.init();
  }

  // Create a detailed landscape image programmatically
  createLandscapeCanvas() {
    this.width = 400;
    this.height = 400;

    const canvas = document.createElement('canvas');
    canvas.width = this.width;
    canvas.height = this.height;
    const ctx = canvas.getContext('2d');

    // === SKY SECTION ===
    const skyGradient = ctx.createLinearGradient(0, 0, 0, this.height * 0.30);
    skyGradient.addColorStop(0, '#8A9DAE');
    skyGradient.addColorStop(0.5, '#A8B8C8');
    skyGradient.addColorStop(1, '#C5D0DB');
    ctx.fillStyle = skyGradient;
    ctx.fillRect(0, 0, this.width, this.height * 0.35);

    // Clouds
    ctx.fillStyle = 'rgba(200, 210, 220, 0.6)';
    for (let i = 0; i < 15; i++) {
      const x = Math.random() * this.width;
      const y = Math.random() * (this.height * 0.20);
      const size = 20 + Math.random() * 40;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }

    // === DISTANT SNOW-CAPPED MOUNTAINS ===
    ctx.fillStyle = '#4A5A6A';
    ctx.beginPath();
    ctx.moveTo(0, this.height * 0.30);

    const mountainPoints = [
      [0, 0.32], [0.05, 0.28], [0.10, 0.30], [0.15, 0.24], [0.20, 0.27],
      [0.25, 0.22], [0.30, 0.25], [0.35, 0.20], [0.40, 0.23], [0.45, 0.18],
      [0.50, 0.21], [0.55, 0.17], [0.60, 0.22], [0.65, 0.19], [0.70, 0.24],
      [0.75, 0.21], [0.80, 0.26], [0.85, 0.23], [0.90, 0.28], [0.95, 0.25], [1.0, 0.30]
    ];

    mountainPoints.forEach(([px, py]) => {
      ctx.lineTo(px * this.width, py * this.height);
    });
    ctx.lineTo(this.width, this.height * 0.50);
    ctx.lineTo(0, this.height * 0.50);
    ctx.closePath();
    ctx.fill();

    // Snow on peaks
    ctx.fillStyle = '#E8EEF4';
    ctx.beginPath();
    ctx.moveTo(0, this.height * 0.35);
    mountainPoints.forEach(([px, py]) => {
      const snowY = py < 0.24 ? py : py + 0.02;
      ctx.lineTo(px * this.width, snowY * this.height);
    });
    ctx.lineTo(this.width, this.height * 0.38);
    ctx.lineTo(0, this.height * 0.38);
    ctx.closePath();
    ctx.fill();

    // === MID-GROUND MOUNTAINS WITH FOREST ===
    // Left mountain slope
    const leftGradient = ctx.createLinearGradient(0, this.height * 0.25, this.width * 0.5, this.height);
    leftGradient.addColorStop(0, '#4A5D4A');
    leftGradient.addColorStop(0.3, '#3D5038');
    leftGradient.addColorStop(0.6, '#2D4028');
    leftGradient.addColorStop(1, '#1D3018');
    ctx.fillStyle = leftGradient;
    ctx.beginPath();
    ctx.moveTo(0, this.height * 0.25);
    ctx.lineTo(0, this.height);
    ctx.lineTo(this.width * 0.48, this.height);
    ctx.lineTo(this.width * 0.42, this.height * 0.70);
    ctx.lineTo(this.width * 0.35, this.height * 0.55);
    ctx.lineTo(this.width * 0.25, this.height * 0.42);
    ctx.lineTo(this.width * 0.15, this.height * 0.35);
    ctx.closePath();
    ctx.fill();

    // Right mountain slope
    const rightGradient = ctx.createLinearGradient(this.width, this.height * 0.25, this.width * 0.5, this.height);
    rightGradient.addColorStop(0, '#3A4A3A');
    rightGradient.addColorStop(0.3, '#2D3D28');
    rightGradient.addColorStop(0.6, '#1D2D18');
    rightGradient.addColorStop(1, '#0D1D08');
    ctx.fillStyle = rightGradient;
    ctx.beginPath();
    ctx.moveTo(this.width, this.height * 0.25);
    ctx.lineTo(this.width, this.height);
    ctx.lineTo(this.width * 0.52, this.height);
    ctx.lineTo(this.width * 0.58, this.height * 0.70);
    ctx.lineTo(this.width * 0.65, this.height * 0.55);
    ctx.lineTo(this.width * 0.75, this.height * 0.42);
    ctx.lineTo(this.width * 0.85, this.height * 0.35);
    ctx.closePath();
    ctx.fill();

    // === WINDING RIVER ===
    ctx.fillStyle = '#6A7A8A';
    ctx.beginPath();
    ctx.moveTo(this.width * 0.48, this.height * 0.38);
    ctx.quadraticCurveTo(this.width * 0.44, this.height * 0.50, this.width * 0.50, this.height * 0.60);
    ctx.quadraticCurveTo(this.width * 0.54, this.height * 0.70, this.width * 0.48, this.height * 0.80);
    ctx.quadraticCurveTo(this.width * 0.52, this.height * 0.90, this.width * 0.50, this.height);
    ctx.lineTo(this.width * 0.54, this.height);
    ctx.quadraticCurveTo(this.width * 0.56, this.height * 0.90, this.width * 0.52, this.height * 0.80);
    ctx.quadraticCurveTo(this.width * 0.58, this.height * 0.70, this.width * 0.54, this.height * 0.60);
    ctx.quadraticCurveTo(this.width * 0.48, this.height * 0.50, this.width * 0.52, this.height * 0.38);
    ctx.closePath();
    ctx.fill();

    // River water
    ctx.fillStyle = '#7ABED4';
    ctx.beginPath();
    ctx.moveTo(this.width * 0.49, this.height * 0.40);
    ctx.quadraticCurveTo(this.width * 0.45, this.height * 0.50, this.width * 0.50, this.height * 0.60);
    ctx.quadraticCurveTo(this.width * 0.53, this.height * 0.70, this.width * 0.49, this.height * 0.80);
    ctx.quadraticCurveTo(this.width * 0.51, this.height * 0.90, this.width * 0.50, this.height);
    ctx.lineTo(this.width * 0.52, this.height);
    ctx.quadraticCurveTo(this.width * 0.54, this.height * 0.90, this.width * 0.51, this.height * 0.80);
    ctx.quadraticCurveTo(this.width * 0.55, this.height * 0.70, this.width * 0.52, this.height * 0.60);
    ctx.quadraticCurveTo(this.width * 0.47, this.height * 0.50, this.width * 0.51, this.height * 0.40);
    ctx.closePath();
    ctx.fill();

    // === DENSE FOREST TREES ===
    // Left side trees
    for (let i = 0; i < 2000; i++) {
      const x = Math.random() * (this.width * 0.46);
      const slopeProgress = x / (this.width * 0.46);
      const minY = this.height * 0.25 + slopeProgress * (this.height * 0.30);
      const y = minY + Math.random() * (this.height - minY - 5);

      if (y > this.height * 0.32) {
        const sunlight = 0.6 + Math.random() * 0.4;
        const r = Math.floor(35 + 25 * sunlight);
        const g = Math.floor(60 + 40 * sunlight);
        const b = Math.floor(25 + 20 * sunlight);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;

        const treeSize = 2 + Math.random() * 4;
        ctx.beginPath();
        ctx.moveTo(x, y - treeSize);
        ctx.lineTo(x - treeSize * 0.6, y + treeSize * 0.5);
        ctx.lineTo(x + treeSize * 0.6, y + treeSize * 0.5);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Right side trees
    for (let i = 0; i < 2000; i++) {
      const x = this.width * 0.54 + Math.random() * (this.width * 0.46);
      const distFromRight = this.width - x;
      const slopeProgress = distFromRight / (this.width * 0.46);
      const minY = this.height * 0.25 + slopeProgress * (this.height * 0.30);
      const y = minY + Math.random() * (this.height - minY - 5);

      if (y > this.height * 0.32) {
        const shade = 0.3 + Math.random() * 0.4;
        const r = Math.floor(20 + 20 * shade);
        const g = Math.floor(40 + 30 * shade);
        const b = Math.floor(15 + 15 * shade);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;

        const treeSize = 2 + Math.random() * 4;
        ctx.beginPath();
        ctx.moveTo(x, y - treeSize);
        ctx.lineTo(x - treeSize * 0.6, y + treeSize * 0.5);
        ctx.lineTo(x + treeSize * 0.6, y + treeSize * 0.5);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Get image data
    const imageData = ctx.getImageData(0, 0, this.width, this.height);
    this.originalColors = Float32Array.from(imageData.data);

    return canvas;
  }

  init() {
    this.createLandscapeCanvas();
    this.setupParticles();
  }

  setupParticles() {
    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(this.count * 3);
    const colors = new Float32Array(this.count * 3);
    const sizes = new Float32Array(this.count);
    const phases = new Float32Array(this.count);

    // Collect valid pixels
    const threshold = 5;
    const validPixels = [];

    for (let i = 0; i < this.width * this.height; i++) {
      const r = this.originalColors[i * 4 + 0];
      const g = this.originalColors[i * 4 + 1];
      const b = this.originalColors[i * 4 + 2];
      const brightness = r * 0.21 + g * 0.72 + b * 0.07;
      if (brightness > threshold) {
        validPixels.push(i);
      }
    }

    // Sample pixels to create particles
    const step = Math.max(1, Math.floor(validPixels.length / this.count));
    let particleIndex = 0;

    for (let j = 0; j < validPixels.length && particleIndex < this.count; j += step) {
      const i = validPixels[j];
      const x = i % this.width;
      const y = Math.floor(i / this.width);

      const r = this.originalColors[i * 4 + 0] / 255;
      const g = this.originalColors[i * 4 + 1] / 255;
      const b = this.originalColors[i * 4 + 2] / 255;

      const i3 = particleIndex * 3;

      // Map pixel position to 3D coordinates
      positions[i3] = (x - this.width / 2) * 0.3;
      positions[i3 + 1] = (this.height / 2 - y) * 0.3;
      positions[i3 + 2] = (Math.random() - 0.5) * 5;

      // Use pixel color
      colors[i3] = r;
      colors[i3 + 1] = g;
      colors[i3 + 2] = b;

      // Wide size variation
      const randomVariation = 0.3 + Math.random() * 3.0;
      sizes[particleIndex] = (0.5 + Math.random() * 10.0) * randomVariation;

      phases[particleIndex] = Math.random() * Math.PI * 2;

      particleIndex++;
    }

    // Fill remaining with random samples
    while (particleIndex < this.count && validPixels.length > 0) {
      const i = validPixels[Math.floor(Math.random() * validPixels.length)];
      const x = i % this.width;
      const y = Math.floor(i / this.width);

      const r = this.originalColors[i * 4 + 0] / 255;
      const g = this.originalColors[i * 4 + 1] / 255;
      const b = this.originalColors[i * 4 + 2] / 255;

      const i3 = particleIndex * 3;

      positions[i3] = (x - this.width / 2) * 0.3 + (Math.random() - 0.5) * 0.3;
      positions[i3 + 1] = (this.height / 2 - y) * 0.3 + (Math.random() - 0.5) * 0.3;
      positions[i3 + 2] = (Math.random() - 0.5) * 5;

      colors[i3] = r;
      colors[i3 + 1] = g;
      colors[i3 + 2] = b;

      const randomVariation = 0.3 + Math.random() * 3.0;
      sizes[particleIndex] = (0.5 + Math.random() * 10.0) * randomVariation;

      phases[particleIndex] = Math.random() * Math.PI * 2;

      particleIndex++;
    }

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    this.uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uSize: { value: 60 }
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexColors: true
    });

    this.points = new THREE.Points(this.geometry, this.material);
  }

  // Use shared shaders for consistent look
  getVertexShader() {
    return SHARED_VERTEX_SHADER;
  }

  getFragmentShader() {
    return SHARED_FRAGMENT_SHADER;
  }

  update(time, deltaTime) {
    this.uniforms.uTime.value = time;
  }

  updateMouse(x, y) {
    this.uniforms.uMouse.value.set(x, y);
  }

  getMesh() {
    return this.points;
  }

  getPoints() {
    return this.points;
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}
